import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GlobalContext, useBranding, isAvMed } from 'context';
import { useUpdatePlan, useContactus } from '@sentara-health/sentara-api-hooks-core';
import { HamburgerMenu } from 'organisms';

import {
  NavigateConst,
  Proxy,
  setLocalStorageBoolean,
  getLocalStorageBoolean,
  apiResponseSafeCheck,
  setEncryptedValue,
  errorhandler,
  MailIcon,
  isGuest,
  isWellnessUser,
} from 'common';
import {
  ButtonLink,
  Image,
  PlanTypeDropdown,
  TopNav,
  SearchIcon,
  Link,
  InputField,
  FAQsIcon,
} from '@sentara-health/optimahealth_web';

export const HeaderTopNav = () => {
  const [guestValue, setGuestValue] = useState<boolean>(false);
  const [wellnessValue, setWellnessValue] = useState<boolean>(false);
  const { ssoEndPoints } = useContext(GlobalContext);
  const memberHomeUrl = ssoEndPoints?.memberHome;
  const { t } = useTranslation();
  useEffect(() => {
    (async () => {
      const guestValue: boolean = await isGuest();
      const wellnessValue: boolean = await isWellnessUser();
      setGuestValue(guestValue);
      setWellnessValue(wellnessValue);
    })();
  }, []);

  return (
    <div>
      <TopNav
        topLinks={[
          {
            dataTestId: 'navLinkProfile',
            label: t('common.myOptimaAccount'),
            url: `${(!guestValue || (guestValue && wellnessValue)) ? '/dashboard' : ''}`,
          },
          {
            dataTestId: 'navLinkMembers',
            label: t('common.memberHome'),
            url: memberHomeUrl,
          },
        ]}
      />
    </div>
  );
};

interface ChoosePlanTypeProps {
  selectedplanType: string;
  planList: any;
  setSelectedplanType: (response: any) => void;
  memberID: string;
}

export const ChoosePlanType = ({
  selectedplanType,
  planList,
  setSelectedplanType,
  memberID,
}: ChoosePlanTypeProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    setIsDesignatedRepresentative,
    isDesignatedRepresentative,
    setUserEncodedData,
    setIsGuestSwitch,
    proxyMemberId,
    setProxyMemberId,
  } = useContext(GlobalContext);
  const DesignatedDropdownLink: boolean = getLocalStorageBoolean(
    Proxy?.DesignatedDropdownLink
  );
  let proxyFor = proxyMemberId ?? '';
  let updatedPlanList: Array<string> = [];
  if (proxyFor !== '' || isDesignatedRepresentative) {
    setSelectedplanType(
      `${t('common.designatedrepresentative')}`?.toUpperCase()
    );
    setLocalStorageBoolean(Proxy?.DesignatedRepresentative, true);
  }
  updatedPlanList = DesignatedDropdownLink
    ? [
        ...planList,
        {
          planName: `${t('common.designatedrepresentative')}`?.toUpperCase(),
          planID: '',
          memberID: '',
        },
      ]
    : [...planList];
  const updatePlan = (e: {
    planName: string;
    planID: string | number;
    memberID: string | number;
  }) => {
    setLocalStorageBoolean(Proxy?.DesignatedRepresentative, false);
    setEncryptedValue(Proxy.MemberId, '');
    setProxyMemberId('');
    setUserEncodedData([]);
    setEncryptedValue(Proxy.memberProxy, '');
    setIsGuestSwitch(false);
    setSelectedplanType(e?.planName);
    if (
      e?.planID === '' &&
      e?.memberID === '' &&
      e?.planName === `${t('common.designatedrepresentative')}`?.toUpperCase()
    ) {
      setIsDesignatedRepresentative(true);
      setLocalStorageBoolean(Proxy?.DesignatedRepresentative, true);
      navigate(NavigateConst.dashboard);
    } else if (
      e?.planID === '' &&
      e?.memberID === '' &&
      e?.planName === t('common.guest')
    ) {
      setIsGuestSwitch(true);
      setIsDesignatedRepresentative(false);
      setLocalStorageBoolean(Proxy?.DesignatedRepresentative, false);
      navigate(NavigateConst.enrollBenefits);
    } else {
      setIsDesignatedRepresentative(false);
      setLocalStorageBoolean(Proxy?.DesignatedRepresentative, false);
      let postDate = {
        planId: e?.planID,
        Id: memberID,
      };
      updateplan(postDate);
    }
  };
  const { UpdatePlanType } = useUpdatePlan();
  const updateplan = async (postDate: any) => {
    const resp = await UpdatePlanType(postDate);
    setEncryptedValue(Proxy.MemberId, '');
    setProxyMemberId('');
    if (resp?.data?.memberId !== '')
      globalThis.location.href = NavigateConst.dashboard;
  };

  return (
    <PlanTypeDropdown
      labeltext={t('common.currentPlan')}
      buttonlabel={selectedplanType}
      dropdownlistitems={updatedPlanList}
      onSelect={(e: any) => updatePlan(e)}
      url="#"
    />
  );
};

interface ContactUsLinkProps {
  showIcon: boolean;
  memberID?: boolean;
  showFAQLink?: boolean;
  onItemClick?: () => void;
}

export const ContactUsLink = ({
  showIcon,
  memberID,
  showFAQLink = false,
  onItemClick,
}: ContactUsLinkProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ContactusType } = useContactus();
  const [contactUsResponse, setContactUsResponse] = useState<any>({});
  const [memberIdValue, setMemberIdValue] = useState<string>('');
  const [emailIdValue, setEmailIdValue] = useState<string>('');
  const { getFeature, getAuthToken, ssoEndPoints } = useContext(GlobalContext);

  useEffect(() => {
    (async () => {
      const isGuestUser: boolean = await isGuest();
      if (
        !isGuestUser &&
        apiResponseSafeCheck(getAuthToken) &&
        !getFeature?.ContactusSalesForce
      ) {
        contactUs();
      }
    })();
  }, [getAuthToken]);

  const contactUs: () => Promise<void> = async () => {
    const response = await ContactusType(false);
    if (response?.data) {
      setContactUsResponse(response?.data ?? '');
      setMemberIdValue(contactUsResponse?.memberId ?? ''); // Initialize memberIdValue
      setEmailIdValue(contactUsResponse?.emailId ?? ''); // Initialize emailIdValue
    } else {
      errorhandler(response?.errorCode);
      setContactUsResponse('');
    }
  };

  // Redirect to Oracle contact us page
  const redirectToContact = (e: Event) => {
    // Prevent the default action of the event
    e.preventDefault();

    // Check if ContactusSalesForce property of getFeature is falsy
    if (!getFeature?.ContactusSalesForce) {
      // If it's falsy, find the form with id 'contactUS' and submit it
      const form = document.getElementById('contactUS') as HTMLFormElement;
      form.submit();
    } else {
      navigate(NavigateConst.homeContactUs);
      onItemClick?.();
    }
  };

  return (
    <>
      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 gap-lg-4">
        {showFAQLink ? (
          <Link
            to={ssoEndPoints?.FAQs}
            className="link-text text-decoration-none d-flex align-items-center contactUs-link mb-4 mb-lg-0"
            data-testid="faqsButton"
            target="_blank"
            rel="noreferrer"
            onClick={() => onItemClick?.()}
          >
            <span className="me-2 d-flex align-items-center">
              {showIcon ? <FAQsIcon width={20} height={20} /> : null}
            </span>
            {t('common.faqs')}
          </Link>
        ): null}
        {!memberID ? (
          <Link
            to={ssoEndPoints?.contactUs}
            className="link-text text-decoration-none d-flex align-items-center contactUs-link"
            data-testid="contactUsButton"
            target="_blank"
            rel="noreferrer"
            onClick={() => onItemClick?.()}
          >
            <span className="me-2 d-flex align-items-center">
              {showIcon ? <MailIcon /> : null}
            </span>
            {t('common.contactUs')}
          </Link>
        ) : (
          <ButtonLink
            buttonText={t('common.contactUs')}
            dataTestId="contactUsButton"
            isHeader
            icon={showIcon ? <MailIcon /> : null}
            onClick={redirectToContact}
            className="mr-3"
          />
        )}
      </div>
      {/* contact us form*/}
      <form
        action={contactUsResponse?.url}
        method="post"
        id="contactUS"
        target="_blank"
        data-testid="contactUS"
      >
        <InputField type="hidden" name="memberid" value={memberIdValue} />
        <InputField type="hidden" name="email" value={emailIdValue} />
        <InputField type="hidden" value="submit" />
      </form>
    </>
  );
};

export const SearchLink = () => {
  return <SearchIcon />;
};

interface HamburgerMenuLinkProps {
  showLogout: boolean;
}
export const HamburgerMenuLink = ({ showLogout }: HamburgerMenuLinkProps) => {
  return <HamburgerMenu showLogout={showLogout} />;
};
const ChangeFavIcon = (src: string) => {
  const link = document.createElement('link');
  const oldLink = document.getElementById('dynamic-favicon');
  link.id = 'dynamic-favicon';
  link.rel = 'shortcut icon';
  link.href = src;
  if (oldLink) {
    document.head.removeChild(oldLink);
  }
  document.head.appendChild(link);
};
export const HeaderLogo = () => {
  const { getAuthToken, ssoEndPoints } = useContext(GlobalContext);
  const { t } = useTranslation();
  const { brandConfig } = useBranding();
  useEffect(() => {
    ChangeFavIcon(brandConfig.image.faviconImage);
  });
  return (
    <Link
      className={isAvMed() ? 'avmed-logo' : 'sentara-logo'}
      to={
        getAuthToken === '' ? (ssoEndPoints?.memberHome || brandConfig.url.brandUrl) : NavigateConst.dashboard
      }
      onClick={() => {
        setEncryptedValue(Proxy.MemberId, '');
      }}
      data-testid="logo"
    >
      <Image
        dataTestId={'optimaLogoImage'}
        path={brandConfig.image.Logo}
        alttext={t('common.optimaHealthLogo')}
      />
    </Link>
  );
};
